/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import MainLayout from '../../components/common/MainLayout';
import type { Farm } from '../../models/Farm';
import {
  useCreateAttributeBindingMutation,
  useCreateCropAttributeMutation,
  useGetAvailableAttributesQuery,
  useGetCropByIdQuery,
  useGetCropAttributeByIdQuery,
  useSetTaxonomyAttributeValueMutation,
  useDeleteCropAttributeBindingMutation,
  useUpdateCropMutation,
  useDeleteCropMutation,
  useGetCropTaxonomyTreeQuery,
} from '../../api/cropsApi';
import {
  useGetPropertiesQuery,
  useGetCropPropertyBindingsQuery,
  useCreateCropPropertyBindingMutation,
  useUpdateCropPropertyBindingMutation,
  useDeleteCropPropertyBindingMutation,
} from '../../api/plantingEventsApi';
import type { CropPropertyBinding } from '../../types/plantingEvents';
import type {
  CropAttributeCreatePayload,
  TaxonomyScope,
} from '../../models/Crop';
import PlantingsList from '../../components/plantings/PlantingsList';
import { toastError, toastSuccess } from '../../utils/toast';
import { CropFormModal } from '../../components/crops/CropFormModal';
import { hasAction } from '../../types/permissions';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import { buildScenarioWorldIndexes, findScenarioCropSummary } from '../../utils/scenarioWorldAdapters';

const styles = {
  flexBetween: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  flexBetweenStart: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }),
  noMargin: css({ margin: 0 }),
  descriptionBlock: css({ width: '100%', marginBottom: 16 }),
  smallText: css({ fontSize: '0.85em' }),
  textCenter: css({ marginBottom: 16, textAlign: 'center' }),
  linkButton: css({ padding: 0, height: 'auto' }),
  xsmallText: css({ fontSize: 12 }),
  eventParametersHelp: css({ marginTop: 8 }),
};

const { Title, Text } = Typography;

const scopeLabelMap: Record<TaxonomyScope, string> = {
  family: 'Семейство',
  genus: 'Род',
  species: 'Вид',
  cultivar: 'Сорт',
  crop: 'Культура',
};

const ScenarioFarmCropDetailsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const navigate = useNavigate();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();
  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const crop = useMemo(
    () => findScenarioCropSummary(world.structure, cropId, indexes, world.runtime?.plantings),
    [cropId, indexes, world.runtime?.plantings, world.structure],
  );

  if (!crop) {
    return (
      <MainLayout>
        <Card>Культура не найдена</Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Фермы</Link> },
            { title: <Link to={`/saves/${saveId}`}>{world.scenario?.title || 'Сценарий'}</Link> },
            { title: <Link to={`/saves/${saveId}/crops`}>Культуры</Link> },
            { title: crop.name },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Alert
          type="info"
          showIcon
          message="Культура сценария"
          description="Детали культуры строятся из посадок текущего scenario world snapshot. Редактирование и привязки для сценария пока отключены."
        />

        <Card
          extra={
            <Space>
              <Button onClick={() => navigate(`/saves/${saveId}/crops/${crop.routeId}/plantings`)}>Посадки</Button>
              <Button onClick={() => { void refreshWorld(); }}>Обновить</Button>
            </Space>
          }
        >
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <div css={styles.flexBetweenStart}>
              <div>
                <Title level={4} css={styles.noMargin}>{crop.name}</Title>
                <Text type="secondary">scenario crop · {crop.routeId}</Text>
              </div>
              <Tag color="blue">{crop.totalQuantity} шт</Tag>
            </div>
            <Descriptions size="small" column={2}>
              <Descriptions.Item label="Посадки">{crop.plantingsCount || 0}</Descriptions.Item>
              <Descriptions.Item label="Грядки">{crop.bedIds.length}</Descriptions.Item>
              <Descriptions.Item label="Участки">{crop.plotIds.length}</Descriptions.Item>
              <Descriptions.Item label="Тип данных">Scenario world</Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title="Посадки культуры">
          {crop.plantings.length === 0 ? (
            <Empty description="Для этой культуры в сценарии пока нет посадок" />
          ) : (
            <PlantingsList
              plantings={crop.plantings}
              groupByCrop={false}
            />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundCropDetailsPage: React.FC = () => {
  const { saveId, cropId } = useParams<{ saveId: string; cropId: string }>();
  const save = useSave();
  const world = useWorld();
  const farmId = save.farmId;
  const navigate = useNavigate();
  const [customAttributeForm] = Form.useForm();
  const [customAttributeModalOpen, setCustomAttributeModalOpen] = useState(false);
  
  const [editAttributeModalOpen, setEditAttributeModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<any>(null);
  const [editingAttributeId, setEditingAttributeId] = useState<number | null>(null);
  const [editAttributeForm] = Form.useForm();

  const [bindAttributeModalOpen, setBindAttributeModalOpen] = useState(false);
  const [bindAttributeForm] = Form.useForm();
  const [selectedAttributeForBinding, setSelectedAttributeForBinding] = useState<number | null>(null);

  // Properties Binding
  const [bindPropertyModalOpen, setBindPropertyModalOpen] = useState(false);
  const [bindPropertyForm] = Form.useForm();
  const [editEventBindingModalOpen, setEditEventBindingModalOpen] = useState(false);
  const [editEventBindingForm] = Form.useForm();
  const [editingEventBinding, setEditingEventBinding] = useState<CropPropertyBinding | null>(null);

  const [editCropModalOpen, setEditCropModalOpen] = useState(false);

  const farm = useMemo<Farm | null>(() => (
    farmId
      ? {
          id: farmId,
          name: save.farmName || 'Сейв',
          is_owner: world.farm?.isOwner ?? false,
          actions: (world.allowedActions?.global || []).map((code) => ({ title: '', code })),
        }
      : null
  ), [farmId, save.farmName, world.allowedActions?.global, world.farm?.isOwner]);
  const farmActions = useMemo(() => farm?.actions ?? [], [farm?.actions]);
  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);

  // Проверка прав на редактирование атрибутов культур
  const canEditCropAttributes = useMemo(() => {
    if (!user) return false;
    // Суперадмин/суперюзер
    if (user.role && ['superadmin', 'superuser'].includes(String(user.role).toLowerCase())) {
      return true;
    }
    // Глобальные права
    if (Array.isArray(userActions)) {
      const hasGlobalPermission = 
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update') ||
        userActions.includes('crops.manage') ||
        userActions.includes('crops.update');
      if (hasGlobalPermission) return true;
    }
    // Владелец фермы
    if (farm?.is_owner) return true;
    // Права на уровне фермы
    if (Array.isArray(farmActions)) {
      const hasFarmPermission = farmActions.some((action: any) => {
        const code = typeof action === 'string' ? action : action.code;
        return (
          code === 'crop_attributes.manage' ||
          code === 'crops.attributes.manage' ||
          code === 'crops.attributes.update' ||
          code === 'crops.manage' ||
          code === 'crops.update' ||
          code === 'farms.update'
        );
      });
      if (hasFarmPermission) return true;
    }
    return false;
  }, [user, userActions, farm, farmActions]);

  const cropQueryArg = cropId ? cropId : skipToken;
  const {
    data: crop,
    isLoading: cropLoading,
    isError: cropError,
    refetch: refetchCrop,
  } = useGetCropByIdQuery(cropQueryArg);

  // Используем атрибуты из crop.attributes вместо отдельных запросов
  const cropAttributes = useMemo(() => crop?.attributes || [], [crop?.attributes]);

  // Загружаем доступные атрибуты для привязки с учетом таксономии (save-scoped).
  const {
    data: availableAttributesFromApi = [],
    isLoading: availableAttributesLoading,
  } = useGetAvailableAttributesQuery(
    cropId && saveId
      ? { scope: 'crop', scopeId: cropId, saveId }
      : skipToken,
  );

  // Атрибуты, которые еще не привязаны к этой культуре
  const availableAttributes = useMemo(() => {
    const boundAttributeIds = new Set(cropAttributes.map((attr) => attr.attributeId));
    return availableAttributesFromApi.filter((attr) => !boundAttributeIds.has(attr.id));
  }, [availableAttributesFromApi, cropAttributes]);

  const [createAttributeBinding, { isLoading: bindingAttribute }] = useCreateAttributeBindingMutation();
  const [setTaxonomyAttributeValue, { isLoading: isUpdatingValue }] = useSetTaxonomyAttributeValueMutation();
  const [createCropAttribute, { isLoading: creatingCustomAttribute }] = useCreateCropAttributeMutation();

  // Properties API hooks
  const { data: properties = [], isLoading: propertiesLoading } = useGetPropertiesQuery('planting');
  const { data: cropEventBindings = [], isLoading: bindingsLoading } = useGetCropPropertyBindingsQuery(
    cropId
      ? { cropId, farmId }
      : skipToken
  );
  const [createEventBinding, { isLoading: creatingEventBinding }] = useCreateCropPropertyBindingMutation();
  const [updateEventBinding, { isLoading: updatingEventBinding }] = useUpdateCropPropertyBindingMutation();
  const [deleteEventBinding] = useDeleteCropPropertyBindingMutation();
  const [deleteCropAttributeBinding] = useDeleteCropAttributeBindingMutation();
  const [updateCrop, { isLoading: updatingCrop }] = useUpdateCropMutation();
  const [deleteCrop] = useDeleteCropMutation();
  
  const { data: taxonomy, isLoading: taxonomyLoading } = useGetCropTaxonomyTreeQuery();
  
  const canUpdateCrops = hasAction(user, 'crops', 'crops.update', farm?.id);
  const canDeleteCrops = hasAction(user, 'crops', 'crops.delete', farm?.id);
  
  const { data: fullAttribute } = useGetCropAttributeByIdQuery(
    editingAttributeId != null && saveId
      ? { id: editingAttributeId, saveId }
      : skipToken
  );

  const { data: fullAttributeForBinding } = useGetCropAttributeByIdQuery(
    selectedAttributeForBinding != null && saveId
      ? { id: selectedAttributeForBinding, saveId }
      : skipToken
  );

  const handleCreateCustomAttribute = async () => {
    if (!cropId) {
      toastError('Культура не определена');
      return;
    }

    try {
      if (!saveId) {
        toastError('Активный сейв не определён');
        return;
      }
      const values = await customAttributeForm.validateFields();
      const payload: CropAttributeCreatePayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        scopeType: 'save',
        options: null,
      };

      const created = await createCropAttribute({ ...payload, saveId }).unwrap();
      const value = values.value?.trim() ?? '';
      if (value) {
        await setTaxonomyAttributeValue({
          scope: 'crop',
          scopeId: cropId,
          attributeId: created.id,
          saveId,
          choiceId: null,
          customValue: value,
        }).unwrap();
      }

      toastSuccess('Атрибут добавлен');
      setCustomAttributeModalOpen(false);
      customAttributeForm.resetFields();
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleOpenEditAttribute = (attr: any) => {
    setEditingAttribute(attr);
    setEditingAttributeId(attr.attributeId);
    
    if (attr.choiceId != null) {
      editAttributeForm.setFieldsValue({ value: { choiceId: String(attr.choiceId) } });
    } else if (attr.customValue) {
      editAttributeForm.setFieldsValue({ value: { customValue: attr.customValue } });
    } else if (attr.value) {
      editAttributeForm.setFieldsValue({ value: { customValue: attr.value } });
    } else {
      editAttributeForm.setFieldsValue({ value: {} });
    }
    
    setEditAttributeModalOpen(true);
  };

  const handleSaveAttributeValue = async () => {
    if (!cropId || !editingAttribute) return;
    
    try {
      const values = await editAttributeForm.validateFields();
      const hasOptions = fullAttribute && Array.isArray(fullAttribute.options) && fullAttribute.options.length > 0;
      
      const payload: any = {
        scope: 'crop' as TaxonomyScope,
        scopeId: cropId,
        attributeId: editingAttribute.attributeId,
        farmId,
      };
      
      if (hasOptions) {
        const choiceId = values.value?.choiceId;
        payload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
        payload.customValue = null;
      } else {
        payload.choiceId = null;
        payload.customValue = values.value?.customValue?.trim() || null;
      }
      
      await setTaxonomyAttributeValue(payload).unwrap();
      toastSuccess('Значение атрибута обновлено');
      setEditAttributeModalOpen(false);
      setEditingAttribute(null);
      setEditingAttributeId(null);
      editAttributeForm.resetFields();
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleBindAttribute = async () => {
    if (!cropId) {
      toastError('Культура не определена');
      return;
    }

    try {
      if (!saveId) {
        toastError('Активный сейв не определён');
        return;
      }
      const values = await bindAttributeForm.validateFields();
      const attributeId = values.attributeId;

      // Создаем привязку
      await createAttributeBinding({
        attributeId,
        scope: 'crop',
        scopeId: cropId,
        saveId,
      }).unwrap();

      // Если указано значение, устанавливаем его
      const hasOptions = fullAttributeForBinding && Array.isArray(fullAttributeForBinding.options) && fullAttributeForBinding.options.length > 0;
      const hasValue = hasOptions
        ? values.value?.choiceId != null
        : values.value?.customValue != null && values.value.customValue.trim() !== '';

      if (hasValue) {
        const valuePayload: any = {
          scope: 'crop' as TaxonomyScope,
          scopeId: cropId,
          attributeId,
          saveId,
        };
        
        if (hasOptions) {
          const choiceId = values.value?.choiceId;
          valuePayload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
          valuePayload.customValue = null;
        } else {
          valuePayload.choiceId = null;
          valuePayload.customValue = values.value?.customValue?.trim() || null;
        }
        
        await setTaxonomyAttributeValue(valuePayload).unwrap();
      }

      toastSuccess('Атрибут привязан к культуре');
      setBindAttributeModalOpen(false);
      bindAttributeForm.resetFields();
      setSelectedAttributeForBinding(null);
      await refetchCrop();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  // Property Bindings handlers
  const availableProperties = useMemo(() => {
    const boundIds = new Set(cropEventBindings.map((b) => b.propertyId));
    return properties.filter((attr) => attr.semanticType === 'measured' && !boundIds.has(attr.id));
  }, [properties, cropEventBindings]);

  const handleBindProperty = async () => {
    if (!cropId) {
      toastError('Культура не определена');
      return;
    }

    try {
      const values = await bindPropertyForm.validateFields();
      await createEventBinding({
        cropId,
        payload: {
          propertyId: values.propertyId,
          isRequired: values.isRequired ?? false,
          includeInPricelist: values.includeInPricelist ?? false,
          sortOrder: values.sortOrder ?? 0,
        },
      }).unwrap();

      toastSuccess('Измеряемый параметр привязан к культуре');
      setBindPropertyModalOpen(false);
      bindPropertyForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleEditEventBinding = (binding: CropPropertyBinding) => {
    setEditingEventBinding(binding);
    editEventBindingForm.setFieldsValue({
      isRequired: binding.isRequired,
      includeInPricelist: binding.includeInPricelist,
      sortOrder: binding.sortOrder,
    });
    setEditEventBindingModalOpen(true);
  };

  const handleUpdateEventBinding = async () => {
    if (!cropId || !editingEventBinding) {
      return;
    }

    try {
      const values = await editEventBindingForm.validateFields();
      await updateEventBinding({
        cropId,
        bindingId: editingEventBinding.id,
        payload: {
          isRequired: values.isRequired,
          includeInPricelist: values.includeInPricelist,
          sortOrder: values.sortOrder,
        },
      }).unwrap();

      toastSuccess('Привязка обновлена');
      setEditEventBindingModalOpen(false);
      setEditingEventBinding(null);
      editEventBindingForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleDeleteEventBinding = async (binding: CropPropertyBinding) => {
    if (!cropId) return;

    Modal.confirm({
      title: 'Удалить привязку?',
      content: `Вы уверены, что хотите удалить привязку параметра "${binding.property.name}"?`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteEventBinding({
            cropId,
            bindingId: binding.id,
          }).unwrap();
          toastSuccess('Привязка удалена');
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const handleEditCrop = () => {
    setEditCropModalOpen(true);
  };

  const handleCropUpdate = async (values: any) => {
    if (!cropId) return;

    const speciesId = Number(values.speciesId);
    const cultivarId =
      values.cultivarId === undefined || values.cultivarId === null
        ? null
        : Number(values.cultivarId);

    try {
      await updateCrop({
        id: cropId,
        payload: {
          speciesId,
          cultivarId,
          latinName: values.latinName,
          name: values.name,
        },
      }).unwrap();
      toastSuccess('Культура обновлена');
      setEditCropModalOpen(false);
      await refetchCrop();
    } catch (error: any) {
      // Error handled by toast
    }
  };

  const handleDeleteCrop = () => {
    Modal.confirm({
      title: `Удалить культуру «${crop?.name}»?`,
      content: 'Это действие необратимо. Все связанные посадки и данные будут удалены.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteCrop(cropId!).unwrap();
          toastSuccess('Культура удалена');
          navigate(`/saves/${saveId}/crops`);
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const handleDeleteCropAttributeBinding = async (attr: any) => {
    if (!cropId) return;

    // Можно удалить только привязки на уровне культуры (cropId есть и bindingId есть)
    if (!attr.cropId || !attr.bindingId) {
      toastError('Можно удалить только привязки на уровне культуры');
      return;
    }

    Modal.confirm({
      title: 'Удалить привязку атрибута?',
      content: `Вы уверены, что хотите удалить привязку атрибута "${attr.name}" от этой культуры? Атрибут останется в системе, но больше не будет связан с этой культурой.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteCropAttributeBinding({
            cropId,
            bindingId: attr.bindingId,
          }).unwrap();
          toastSuccess('Привязка атрибута удалена');
        } catch (error: any) {
          toastError(error);
        }
      },
    });
  };

  const renderAttributeCard = (attr: any) => {
    const key = String(attr.attributeId);
    const scopeLabel = attr.scope ? scopeLabelMap[attr.scope as TaxonomyScope] : '—';
    const currentValue = attr.value || '—';
    const isCropLevelBinding = !!attr.cropId && !!attr.bindingId;

    return (
      <Descriptions.Item key={key} label={attr.name} span={3}>
        <div css={styles.flexBetween}>
          <Space size={4}>
            <Text>{currentValue}</Text>
            {attr.cropId ? (
              <Tag color="purple" css={styles.noMargin}>Культура</Tag>
            ) : (
              <Tag color="cyan" css={styles.noMargin}>{scopeLabel}</Tag>
            )}
          </Space>
          {canEditCropAttributes && (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEditAttribute(attr)}
              >
                Изменить
              </Button>
              {isCropLevelBinding && (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteCropAttributeBinding(attr)}
                >
                  Удалить привязку
                </Button>
              )}
            </Space>
          )}
        </div>
      </Descriptions.Item>
    );
  };

  const farmName = farm?.name ?? (farmId ? `Ферма ${farmId}` : 'Ферма');

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Фермы</Link> },
            { title: <Link to={`/saves/${saveId}`}>{farmName}</Link> },
            { title: <Link to={`/saves/${saveId}/crops`}>Культуры</Link> },
            { title: crop?.name ?? 'Культура' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          {cropLoading ? (
            <Spin />
          ) : cropError || !crop ? (
            <Empty description="Культура не найдена" />
          ) : (
            <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
              <div css={styles.flexBetweenStart}>
                <div>
                  <Title level={4} css={styles.noMargin}>
                    {crop.name}
                  </Title>
                  <Text type="secondary" italic>{crop.latinName}</Text>
                </div>
                <Space>
                  {canUpdateCrops && (
                    <Button icon={<EditOutlined />} onClick={handleEditCrop}>
                      Изменить
                    </Button>
                  )}
                  {canDeleteCrops && (
                    <Button danger icon={<DeleteOutlined />} onClick={handleDeleteCrop}>
                      Удалить
                    </Button>
                  )}
                </Space>
              </div>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Вид">{crop.speciesName || crop.speciesId || '—'}</Descriptions.Item>
                <Descriptions.Item label="Сорт">{crop.cultivarName || crop.cultivarId || '—'}</Descriptions.Item>
              </Descriptions>
              {canEditCropAttributes && (
                <Space wrap>
                  <Button onClick={() => navigate(`/saves/${saveId}/crops`)}>К списку культур</Button>
                </Space>
              )}
            </Space>
          )}
        </Card>

        <Card 
          title="Постоянные характеристики культуры"
          extra={
            canEditCropAttributes && (
              <Button
                size="small"
                onClick={() => {
                  bindAttributeForm.resetFields();
                  setSelectedAttributeForBinding(null);
                  setBindAttributeModalOpen(true);
                }}
                disabled={cropLoading || !crop || availableAttributes.length === 0}
              >
                Привязать атрибут
              </Button>
            )
          }
        >
          <Space direction="vertical" size={8} css={styles.descriptionBlock}>
            <Text type="secondary">
              <strong>Crop Attributes</strong> — это постоянные свойства вида/сорта (цвет хвои, форма кроны, зимостойкость и т.д.), 
              которые не меняются для конкретных экземпляров растений.
            </Text>
          </Space>
          {cropLoading ? (
            <Spin />
          ) : cropAttributes.length === 0 ? (
            <Empty description="Для этой культуры нет доступных атрибутов" />
          ) : (
            <Descriptions bordered size="small" column={1}>
              {cropAttributes.map((attr) => renderAttributeCard(attr))}
            </Descriptions>
          )}
        </Card>

        <Card 
          title="Измеряемые параметры для посадок этой культуры"
          extra={
            canEditCropAttributes && (
              <Button
                size="small"
                onClick={() => {
                  bindPropertyForm.resetFields();
                  setBindPropertyModalOpen(true);
                }}
                disabled={cropLoading || !crop || availableProperties.length === 0}
              >
                Привязать параметр
              </Button>
            )
          }
        >
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <Text type="secondary">
              <strong>Properties</strong> — это динамические измерения для конкретных экземпляров посадок 
              (высота растения, здоровье, результаты осмотра и т.д.). 
              Здесь вы можете выбрать, какие параметры будут доступны для измерения в посадках этой культуры.
            </Text>
            
            {bindingsLoading ? (
              <Spin />
            ) : cropEventBindings.length === 0 ? (
              <Empty description="Нет привязанных параметров. Привяжите нужные параметры для измерения." />
            ) : (
              <Descriptions bordered size="small" column={1}>
                {[...cropEventBindings]
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((binding) => (
                    <Descriptions.Item 
                      key={binding.id} 
                      label={
                        <Space size={4}>
                          <Text>{binding.property.name}</Text>
                          {binding.property.unit && (
                            <Text type="secondary" css={styles.smallText}>
                              ({binding.property.unit})
                            </Text>
                          )}
                        </Space>
                      }
                      span={3}
                    >
                      <div css={styles.flexBetween}>
                        <Space size={4}>
                          {binding.isRequired && <Tag color="red">Обязательный</Tag>}
                          {binding.includeInPricelist && <Tag color="green">В прайслисте</Tag>}
                          <Text type="secondary" css={styles.smallText}>
                            Код: {binding.property.code}
                          </Text>
                        </Space>
                        {canEditCropAttributes && (
                          <Space size={8}>
                            <Button
                              type="link"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEditEventBinding(binding)}
                            >
                              Настроить
                            </Button>
                            <Button
                              type="link"
                              size="small"
                              danger
                              onClick={() => handleDeleteEventBinding(binding)}
                            >
                              Удалить
                            </Button>
                          </Space>
                        )}
                      </div>
                    </Descriptions.Item>
                  ))}
              </Descriptions>
            )}

            <Text type="secondary" css={styles.eventParametersHelp}>
              <strong>Как использовать:</strong> При создании события для посадки этой культуры 
              будут доступны только привязанные здесь параметры.
            </Text>
          </Space>
        </Card>
      </Space>

      {/* Модальное окно для привязки атрибута */}
      <Modal
        title="Привязать атрибут к культуре"
        open={bindAttributeModalOpen}
        onOk={handleBindAttribute}
        onCancel={() => {
          setBindAttributeModalOpen(false);
          bindAttributeForm.resetFields();
          setSelectedAttributeForBinding(null);
        }}
        okText="Привязать"
        cancelText="Отмена"
        confirmLoading={bindingAttribute}
      >
        <Form form={bindAttributeForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Атрибут"
            name="attributeId"
            rules={[{ required: true, message: 'Выберите атрибут' }]}
          >
            <Select
              placeholder="Выберите атрибут"
              loading={availableAttributesLoading}
              showSearch
              optionFilterProp="label"
              onChange={(value) => setSelectedAttributeForBinding(value)}
              options={availableAttributes.map((attr) => ({
                value: attr.id,
                label: attr.description ? `${attr.name} — ${attr.description}` : attr.name,
              }))}
            />
          </Form.Item>
          {selectedAttributeForBinding && fullAttributeForBinding && (
            <>
              {Array.isArray(fullAttributeForBinding.options) && fullAttributeForBinding.options.length > 0 ? (
                <Form.Item
                  label="Значение (необязательно)"
                  name={['value', 'choiceId']}
                >
                  <Select
                    placeholder="Выберите значение"
                    allowClear
                    optionFilterProp="label"
                    options={fullAttributeForBinding.options.map((option: any) => ({
                      value: String(option.id),
                      label: option.code && option.code !== option.title
                        ? `${option.title} (${option.code})`
                        : option.title,
                    }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  label="Значение (необязательно)"
                  name={['value', 'customValue']}
                >
                  <Input placeholder="Введите значение" allowClear />
                </Form.Item>
              )}
              {fullAttributeForBinding.description && (
                <Typography.Text type="secondary" css={commonStyles.smallText}>
                  {fullAttributeForBinding.description}
                </Typography.Text>
              )}
            </>
          )}
        </Form>
        <div css={styles.textCenter}>
            <Text type="secondary">
              Не нашли подходящий атрибут?{' '}
              <Button
                type="link"
                css={styles.linkButton}
                onClick={() => {
                  setBindAttributeModalOpen(false);
                  bindAttributeForm.resetFields();
                  setSelectedAttributeForBinding(null);
                  customAttributeForm.resetFields();
                  setCustomAttributeModalOpen(true);
                }}
              >
                Создайте новый
              </Button>
            </Text>
          </div>
      </Modal>

      {/* Модальное окно для редактирования значения атрибута */}
      <Modal
        title={`Редактировать значение: ${fullAttribute?.name ?? editingAttribute?.name ?? ''}`}
        open={editAttributeModalOpen}
        onOk={handleSaveAttributeValue}
        onCancel={() => {
          setEditAttributeModalOpen(false);
          editAttributeForm.resetFields();
          setEditingAttribute(null);
          setEditingAttributeId(null);
        }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={isUpdatingValue}
      >
        <Form form={editAttributeForm} layout="vertical" preserve={false}>
          {fullAttribute && Array.isArray(fullAttribute.options) && fullAttribute.options.length > 0 ? (
            <Form.Item
              label="Значение"
              name={['value', 'choiceId']}
              rules={[{ required: true, message: 'Выберите значение' }]}
            >
              <Select
                placeholder="Выберите значение"
                allowClear
                optionFilterProp="label"
                options={fullAttribute.options.map((option: any) => ({
                  value: String(option.id),
                  label: option.code && option.code !== option.title
                    ? `${option.title} (${option.code})`
                    : option.title,
                }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label="Значение"
              name={['value', 'customValue']}
              rules={[{ required: true, message: 'Введите значение' }]}
            >
              <Input placeholder="Введите значение" allowClear />
            </Form.Item>
          )}
          {fullAttribute?.description && (
            <Typography.Text type="secondary" css={styles.xsmallText}>
              {fullAttribute.description}
            </Typography.Text>
          )}
        </Form>
      </Modal>

      <Modal
        title="Создание нового атрибута культуры"
        open={customAttributeModalOpen}
        onOk={handleCreateCustomAttribute}
        onCancel={() => {
          setCustomAttributeModalOpen(false);
          customAttributeForm.resetFields();
        }}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={creatingCustomAttribute}
      >
        <Form form={customAttributeForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Название"
            name="name"
            rules={[{ required: true, message: 'Введите название атрибута' }]}
          >
            <Input placeholder="Например, Тип полива" />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} placeholder="Опишите атрибут" allowClear />
          </Form.Item>
          <Form.Item
            label="Значение"
            name="value"
            rules={[{ required: true, message: 'Введите значение атрибута' }]}
          >
            <Input placeholder="Введите значение" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для привязки property */}
      <Modal
        title="Привязать измеряемый параметр"
        open={bindPropertyModalOpen}
        onOk={handleBindProperty}
        onCancel={() => {
          setBindPropertyModalOpen(false);
          bindPropertyForm.resetFields();
        }}
        okText="Привязать"
        cancelText="Отмена"
        confirmLoading={creatingEventBinding}
      >
        <Form form={bindPropertyForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Параметр"
            name="propertyId"
            rules={[{ required: true, message: 'Выберите параметр' }]}
          >
            <Select
              placeholder="Выберите параметр"
              loading={propertiesLoading}
              showSearch
              optionFilterProp="label"
              options={availableProperties.map((attr) => ({
                value: attr.id,
                label: `${attr.name}${attr.unitId ? ` (${attr.unitId})` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Обязательный параметр"
            name="isRequired"
            valuePropName="checked"
            tooltip="Если включено, параметр нужно будет заполнять при создании события"
          >
            <Select
              options={[
                { value: false, label: 'Нет' },
                { value: true, label: 'Да' },
              ]}
              defaultValue={false}
            />
          </Form.Item>
          <Form.Item
            label="Включить в прайслист"
            name="includeInPricelist"
            valuePropName="checked"
            tooltip="Если включено, значение параметра будет показываться в экспортируемом прайслисте"
          >
            <Select
              options={[
                { value: false, label: 'Нет' },
                { value: true, label: 'Да' },
              ]}
              defaultValue={false}
            />
          </Form.Item>
          <Form.Item
            label="Порядок сортировки"
            name="sortOrder"
            tooltip="Определяет порядок отображения параметров (меньшее число = выше)"
          >
            <Input type="number" placeholder="0" defaultValue={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно для редактирования настроек привязки */}
      <Modal
        title={`Настройки: ${editingEventBinding?.property.name ?? ''}`}
        open={editEventBindingModalOpen}
        onOk={handleUpdateEventBinding}
        onCancel={() => {
          setEditEventBindingModalOpen(false);
          setEditingEventBinding(null);
          editEventBindingForm.resetFields();
        }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={updatingEventBinding}
      >
        <Form form={editEventBindingForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Обязательный параметр"
            name="isRequired"
            tooltip="Если включено, параметр нужно будет заполнять при создании события"
          >
            <Select
              options={[
                { value: false, label: 'Нет' },
                { value: true, label: 'Да' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Включить в прайслист"
            name="includeInPricelist"
            tooltip="Если включено, значение параметра будет показываться в экспортируемом прайслисте"
          >
            <Select
              options={[
                { value: false, label: 'Нет' },
                { value: true, label: 'Да' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Порядок сортировки"
            name="sortOrder"
            tooltip="Определяет порядок отображения параметров (меньшее число = выше)"
          >
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>

      <CropFormModal
        open={editCropModalOpen}
        editingCrop={crop || null}
        taxonomy={taxonomy}
        taxonomyLoading={taxonomyLoading}
        onSubmit={handleCropUpdate}
        onCancel={() => setEditCropModalOpen(false)}
        isLoading={updatingCrop}
      />
    </MainLayout>
  );
};

const FarmCropDetailsPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioFarmCropDetailsPage />;
  }
  return <FarmBoundCropDetailsPage />;
};

export default FarmCropDetailsPage;
